// @flow
import { useState } from 'react';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import { NoData, Portal, useQaAgentReportsQuery } from '../../../shared';
import { Preloader } from '@coverright/features';
import _ from 'lodash';
import { ScoreText, scoreColor } from '../ui/qa-shared';
import { QaSparkline } from '../ui/qa-sparkline';

type Props = {
  onSelectAgent: (agentId: string) => void;
};

export const TeamOverview = ({ onSelectAgent }: Props) => {
  const { data, loading } = useQaAgentReportsQuery({ fetchPolicy: 'cache-and-network' });
  const [desc, setDesc] = useState(true);

  if (loading && !data) return <Preloader sx={{ height: 300 }} />;
  const rows = _.orderBy(data?.qaAgentReports || [], r => r.summary.averageScore, desc ? 'desc' : 'asc');
  if (!rows.length) return <NoData>No agents with QA data.</NoData>;

  return (
    <Portal p={2}>
      <Stack direction={'row'} sx={{ borderBottom: '1px solid #eee', pb: 1, px: 1 }}>
        <Typography variant={'caption'} color={'text.secondary'} flex={1}>Agent</Typography>
        <Typography variant={'caption'} color={'text.secondary'} width={110} sx={{ cursor: 'pointer' }}
                    onClick={() => setDesc(d => !d)}>Avg score {desc ? 'â†“' : 'â†‘'}</Typography>
        <Typography variant={'caption'} color={'text.secondary'} width={90} textAlign={'right'}>Calls scored</Typography>
        <Typography variant={'caption'} color={'text.secondary'} width={120} textAlign={'center'}>Trend</Typography>
        <Typography variant={'caption'} color={'text.secondary'} flex={1}>Top failed question</Typography>
      </Stack>

      {rows.map(r => {
        const top = r.mostFailedQuestions[0];
        return (
          <Stack key={r.agentId} direction={'row'} alignItems={'center'}
                 sx={{ py: 1.5, px: 1, cursor: 'pointer', borderBottom: '1px solid #f5f5f5', '&:hover': { bgcolor: '#f6fafb' } }}
                 onClick={() => r.agentId && onSelectAgent(r.agentId)}>
            <Stack direction={'row'} alignItems={'center'} gap={1} flex={1}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#2d969f', fontSize: 13 }}>
                {(r.agentName || '?').split(' ').map(w => w[0]).slice(0, 2).join('')}
              </Avatar>
              <Typography variant={'body2'} fontWeight={600}>{r.agentName || 'Unknown'}</Typography>
            </Stack>
            <Box width={110}><ScoreText score={r.summary.averageScore} variant={'body1'} /></Box>
            <Typography variant={'body2'} width={90} textAlign={'right'}>{r.summary.callsScored}</Typography>
            <Box width={120} px={1}>
              <QaSparkline points={r.summary.trend} color={scoreColor(r.summary.averageScore)} />
            </Box>
            <Typography variant={'body2'} flex={1} color={'text.secondary'}>
              {top ? <><Box component={'span'} sx={{ color: '#9CA3AF', fontWeight: 600, mr: 0.5 }}>{top.questionId}</Box>{top.question}</> : '-'}
            </Typography>
          </Stack>
        );
      })}

      <Typography variant={'caption'} color={'text.secondary'} sx={{ mt: 1, display: 'block' }}>
        Click an agent to open their My Scorecard view.
      </Typography>
    </Portal>
  );
};